import { computed, effect, Injectable, signal } from '@angular/core';

/**
 * How the app decides which theme to paint.
 * `system` follows the device's `prefers-color-scheme` setting.
 */
export type ThemeMode = 'light' | 'dark' | 'system';

/**
 * The dark palettes on offer. The value is written verbatim to
 * `<html data-theme="...">` and matches a selector in `src/theme.css`.
 */
export type DarkVariant = 'dark-deepsea' | 'dark-slate' | 'dark-abyss' | 'dark-nightwatch';

/** What actually ends up on the `data-theme` attribute. */
export type AppliedTheme = 'light' | DarkVariant;

/** Presentation metadata for the picker on the profile screen. */
export interface DarkVariantOption {
    id: DarkVariant;
    name: string;
    blurb: string;
    /** Three representative colours: page background, surface, accent. */
    swatch: [string, string, string];
}

export const DARK_VARIANTS: DarkVariantOption[] = [
    {
        id: 'dark-deepsea',
        name: 'Deep Sea',
        blurb: 'Navy-tinted dark.',
        swatch: ['#071620', '#0e2130', '#ff7f6e'],
    },
    {
        id: 'dark-slate',
        name: 'Slate',
        blurb: 'Neutral greys with no colour cast.',
        swatch: ['#121212', '#1e1e1e', '#ff8072'],
    },
    {
        id: 'dark-abyss',
        name: 'Abyss',
        blurb: 'True black. Saves battery on OLED screens and reads well in bright sun.',
        swatch: ['#000000', '#0b0b0b', '#ff8474'],
    },
    {
        id: 'dark-nightwatch',
        name: 'Night Watch',
        blurb: 'Warm amber with almost no blue light.',
        swatch: ['#120c07', '#1c1209', '#ff8c42'],
    },
];

/** localStorage keys. Kept in sync with the pre-boot script in `src/index.html`. */
const MODE_KEY = 'elog_theme_mode';
const VARIANT_KEY = 'elog_theme_variant';

const DEFAULT_MODE: ThemeMode = 'light';
const DEFAULT_VARIANT: DarkVariant = 'dark-deepsea';

/** Brand colour painted into the browser/OS chrome per theme. */
const THEME_COLOR: Record<AppliedTheme, string> = {
    light: '#005d87',
    'dark-deepsea': '#0a2d43',
    'dark-slate': '#1f1f1f',
    'dark-abyss': '#000000',
    'dark-nightwatch': '#2a1a0a',
};

function isDarkVariant(value: string | null): value is DarkVariant {
    return DARK_VARIANTS.some((v) => v.id === value);
}

function isThemeMode(value: string | null): value is ThemeMode {
    return value === 'light' || value === 'dark' || value === 'system';
}

/**
 * Owns the user's appearance preference and keeps the `data-theme` /
 * `data-bs-theme` attributes on `<html>` in step with it.
 *
 * The preference is deliberately stored in `localStorage` rather than in the
 * user's PouchDB document: it is a per-device display setting (a phone used on
 * deck at night and a wheelhouse tablet want different answers), and it has to
 * be readable synchronously before login and before storage opens.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
    /** Light, dark, or follow the device. */
    readonly mode = signal<ThemeMode>(DEFAULT_MODE);

    /** Which dark palette to use when a dark theme is showing. */
    readonly darkVariant = signal<DarkVariant>(DEFAULT_VARIANT);

    /** Tracks `prefers-color-scheme: dark` so `system` mode can react to it. */
    private readonly systemPrefersDark = signal<boolean>(false);

    /** The theme actually painted right now. */
    readonly appliedTheme = computed<AppliedTheme>(() => {
        const mode = this.mode();
        const dark = mode === 'dark' || (mode === 'system' && this.systemPrefersDark());
        return dark ? this.darkVariant() : 'light';
    });

    /** True whenever any dark palette is showing. */
    readonly isDark = computed<boolean>(() => this.appliedTheme() !== 'light');

    readonly variants = DARK_VARIANTS;

    constructor() {
        this.restore();
        this.watchSystemPreference();

        // Single place where the DOM is touched; every signal change funnels here.
        effect(() => this.apply(this.appliedTheme()));
    }

    /** Light / dark / follow-the-device. */
    setMode(mode: ThemeMode): void {
        this.mode.set(mode);
        this.persist(MODE_KEY, mode);
    }

    /**
     * Choose a dark palette. Selecting a variant while in light mode only stores
     * the choice — it does not switch the app to dark.
     */
    setDarkVariant(variant: DarkVariant): void {
        this.darkVariant.set(variant);
        this.persist(VARIANT_KEY, variant);
    }

    /**
     * Flip between light and dark. If the user was on `system`, resolve to the
     * opposite of whatever the device is currently showing.
     */
    toggle(): void {
        this.setMode(this.isDark() ? 'light' : 'dark');
    }

    /** Metadata for the currently selected dark palette. */
    currentVariantOption(): DarkVariantOption {
        const id = this.darkVariant();
        return DARK_VARIANTS.find((v) => v.id === id) ?? DARK_VARIANTS[0];
    }

    /**
     * Read the stored preference. Falls back to the defaults when storage is
     * unavailable (private browsing) or holds a value we no longer recognise.
     */
    private restore(): void {
        try {
            const mode = localStorage.getItem(MODE_KEY);
            if (isThemeMode(mode)) {
                this.mode.set(mode);
            }

            const variant = localStorage.getItem(VARIANT_KEY);
            if (isDarkVariant(variant)) {
                this.darkVariant.set(variant);
            }
        } catch {
            // Storage blocked — stay on the defaults.
        }
    }

    private persist(key: string, value: string): void {
        try {
            localStorage.setItem(key, value);
        } catch {
            // Preference simply will not survive a reload; not worth surfacing.
        }
    }

    private watchSystemPreference(): void {
        if (typeof window === 'undefined' || !window.matchMedia) {
            return;
        }

        const query = window.matchMedia('(prefers-color-scheme: dark)');
        this.systemPrefersDark.set(query.matches);
        query.addEventListener('change', (event) => this.systemPrefersDark.set(event.matches));
    }

    /**
     * Write the theme onto `<html>`. `data-theme` drives our own tokens,
     * `data-bs-theme` flips Bootstrap 5.3's built-in dark palette, and the
     * `theme-color` meta tag colours the browser and OS chrome around the PWA.
     */
    private apply(theme: AppliedTheme): void {
        if (typeof document === 'undefined') {
            return;
        }

        const root = document.documentElement;
        root.setAttribute('data-theme', theme);
        root.setAttribute('data-bs-theme', theme === 'light' ? 'light' : 'dark');

        const meta = document.querySelector('meta[name="theme-color"]');
        if (meta) {
            meta.setAttribute('content', THEME_COLOR[theme]);
        }
    }
}
